import { useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { Loader2 } from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

// v7.04 — login restyled to the admoji.com/ads marketing look (Dan's ask).
// v7.05 — tokens moved to ./auth-theme so all four auth pages share one
// source of truth (they had drifted three separate ways before).
import {
  PAGE, CARD, INK, MUTED, LINE, FIELD, BTN, BTN_HOVER, PINK, SANS, SERIF, MONO,
} from "./auth-theme";

export default function Login({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [, setLocation] = useLocation();
  const submittingRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || "Login failed");
        return;
      }

      onLogin();
      setLocation("/brands");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const fieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.outline = "none";
    e.target.style.borderColor = PINK;
    e.target.style.boxShadow = "0 0 0 3px rgba(169,49,91,0.15)";
    e.target.style.backgroundColor = CARD;
  };
  const fieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = LINE;
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = FIELD;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ backgroundColor: PAGE, fontFamily: SANS }}
      data-testid="login-page"
    >
      <div className="w-full" style={{ maxWidth: 400 }}>
        {/* Logo sits above the card, matching the marketing header */}
        <div className="flex justify-center mb-7" data-testid="text-logo">
          <img
            src="/admoji-logo.png"
            alt="admoji"
            width={126}
            style={{ height: "auto", display: "block" }}
          />
        </div>

        <div
          style={{
            backgroundColor: CARD,
            border: `1px solid ${LINE}`,
            borderRadius: 14,
            padding: "34px 32px 30px",
          }}
        >
          <div className="mb-6">
            <div
              style={{
                fontFamily: MONO,
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                marginBottom: 10,
              }}
            >
              Ad Manager
            </div>
            <h1
              style={{
                fontFamily: SANS,
                fontSize: 34,
                fontWeight: 600,
                letterSpacing: "-0.032em",
                lineHeight: 1.08,
                color: INK,
                margin: 0,
              }}
            >
              Welcome{" "}
              <span style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 400, fontSize: 37 }}>
                back.
              </span>
            </h1>
            <p style={{ fontSize: 13.5, color: MUTED, marginTop: 8 }}>
              Sign in to keep building.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                className="text-sm px-4 py-3"
                style={{
                  backgroundColor: "rgba(169,49,91,0.07)",
                  color: "#8F2748",
                  border: "1px solid rgba(169,49,91,0.32)",
                  borderRadius: 9,
                }}
                data-testid="text-login-error"
              >
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="block mb-1.5"
                style={{ fontSize: 12.5, fontWeight: 500, color: MUTED }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                data-testid="input-email"
                className="w-full h-11 px-3 outline-none transition-all"
                style={{
                  backgroundColor: FIELD,
                  border: `1px solid ${LINE}`,
                  borderRadius: 9,
                  color: INK,
                  fontSize: 14,
                  fontFamily: SANS,
                }}
                onFocus={fieldFocus}
                onBlur={fieldBlur}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block mb-1.5"
                style={{ fontSize: 12.5, fontWeight: 500, color: MUTED }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                data-testid="input-password"
                className="w-full h-11 px-3 outline-none transition-all"
                style={{
                  backgroundColor: FIELD,
                  border: `1px solid ${LINE}`,
                  borderRadius: 9,
                  color: INK,
                  fontSize: 14,
                  fontFamily: SANS,
                }}
                onFocus={fieldFocus}
                onBlur={fieldBlur}
              />
            </div>

            <div className="-mt-1 text-right">
              <Link
                href="/forgot-password"
                style={{ fontSize: 12, fontWeight: 500, color: PINK }}
                data-testid="link-forgot-password"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="button-login"
              className="w-full flex items-center justify-center transition-colors disabled:opacity-60"
              style={{
                height: 46,
                backgroundColor: BTN,
                color: "#FFFFFF",
                border: "1px solid transparent",
                borderRadius: 11,
                fontSize: 14.5,
                fontWeight: 600,
                fontFamily: SANS,
              }}
              onMouseEnter={(e) => {
                if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = BTN_HOVER;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = BTN;
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </button>

            <div className="flex items-center gap-3 pt-1">
              <div className="h-px flex-1" style={{ backgroundColor: LINE }} />
              <span style={{ fontSize: 11, color: MUTED }}>or</span>
              <div className="h-px flex-1" style={{ backgroundColor: LINE }} />
            </div>
            <GoogleSignInButton
              theme="outline"
              onSuccess={() => { onLogin(); setLocation("/brands"); }}
              onError={setError}
            />

            <p className="text-center" style={{ fontSize: 12.5, color: MUTED }}>
              Don't have an account?{" "}
              <Link href="/signup" style={{ fontWeight: 600, color: PINK }}>
                Sign up
              </Link>
            </p>
          </form>
        </div>

        <p className="text-center mt-6" style={{ fontSize: 11.5, color: MUTED }}>
          Admoji Ad Manager
        </p>
      </div>
    </div>
  );
}
